import React from 'react';
import {
  ProfileWebPage,
  useProgressiveSessionStore,
} from '@fe-progressive/package-progressive/profile-web';
import {showProgressiveHostToast} from '@fe-progressive/package-progressive/progressive-toast';
import {copyText, goBack, goTo} from '@/utils';
import globalStore from '@/services/global.state';
import {postUserInfo} from '@/services/global.service';
import {useTranslation} from 'react-i18next';
import {
  getDefaultAvatar,
  updateProfile,
  uploadProfilePhoto,
} from './pofile.service';
import {
  defaultHeaderImg,
  edit,
  pic,
  profileImg,
  rightIcon,
} from './profile.variable';
import {AddImg, CopyImg, IDIcon, LockIcon, SmartPhone} from './svg.variable';
import VipSmall from '../vip/vip-small';
import {getVipConfigListV2} from '../vip/vip.service';
import {getCurrentVip} from '@/utils/vip';
import {useWithdrawProgressiveViewportHeightStyle} from '../withdraw/withdraw-progressive-theme';
import '../vip/vip-profile-progressive.scss';

const ProfileProgressive = () => {
  const {i18n, t} = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [user, setUser] = React.useState<any>(null);
  const [avatars, setAvatars] = React.useState<string[]>([]);
  const [level, setLevel] = React.useState(0);
  const progressiveSession = useProgressiveSessionStore;
  const viewportHeightStyle = useWithdrawProgressiveViewportHeightStyle();

  const syncUser = React.useCallback(async () => {
    const res = await postUserInfo();
    setUser(res);
    globalStore.userInfo = res;
    progressiveSession.getState().syncSession({
      isLoggedIn: Boolean(globalStore.token),
      tokenReady: true,
      userInfo: res,
    });
    return res;
  }, [progressiveSession]);

  React.useEffect(() => {
    setLoading(true);
    Promise.all([
      getDefaultAvatar().then(res => setAvatars((res as any) || [])),
      syncUser(),
      getVipConfigListV2().then(res => {
        const {currentVip} = getCurrentVip(res);
        setLevel(currentVip?.level || 0);
      }),
    ]).finally(() => setLoading(false));
  }, [syncUser]);

  const handleUploadAvatar = async (file: File) => {
    setLoading(true);
    try {
      const avatar = (await uploadProfilePhoto({file})) as any;
      await updateProfile(user?.userName, avatar as string);
      await syncUser();
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAvatar = async (avatarUrl: string) => {
    setLoading(true);
    try {
      await updateProfile(user?.userName, avatarUrl);
      await syncUser();
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUserName = async (userName: string) => {
    await updateProfile(userName, '');
    await syncUser();
  };

  return (
    <div
      className="vip-profile-progressive-page overflow-hidden bg-bg"
      style={viewportHeightStyle}>
      <ProfileWebPage
        user={user}
        avatarOptions={avatars}
        loading={loading}
        locale={i18n.language}
        shouldOffsetUserId={true}
        vipSummary={{level}}
        assets={{
          background: profileImg,
          defaultAvatar: defaultHeaderImg,
          editIcon: edit,
          rightIcon,
          uploadIcon: <img src={pic} alt="" className="size-12" />,
          phoneIcon: <SmartPhone width={16} height={16} />,
          lockIcon: <LockIcon width={16} height={16} />,
          idIcon: <IDIcon width={16} height={16} />,
          copyIcon: <CopyImg width={16} height={16} />,
        }}
        uploadButtonSlot={
          <AddImg height={16} radius={50} fill="currentColor" />
        }
        onBack={goBack}
        onCopyUserId={displayUserId => {
          copyText(displayUserId);
          showProgressiveHostToast({
            type: 'success',
            message: t('share.copy-success'),
          });
        }}
        onSelectAvatar={handleSelectAvatar}
        onUploadAvatar={handleUploadAvatar}
        onSaveUserName={handleSaveUserName}
        onGoSetPassword={() => goTo('SetPassword')}
        renderVipBadge={() => (
          <VipSmall level={level} className="h-[1.25rem]" />
        )}
      />
    </div>
  );
};

export default ProfileProgressive;
